import type { FunctionComponent } from 'react'
import React from 'react'
import { Select, Input } from '@arvoreeducacao/bonsai-ds'
import { Modal, RightButton, Text, Wrapper } from './ClassActions.styles'
import { useClassModal } from './ClassActions.hooks'
import { ModalTypes } from '../constants'
import type { Degree } from 'api/data.types'
import type { ApolloClient } from '@apollo/client'

import type { LanguageTokens } from 'i18n'
import { useTranslation } from 'i18n'
import usePermissions from 'utils/hooks/use-permissions'
import useFeatureToggles from 'utils/hooks/use-feature-toggles'

const shouldShowSchoolYearSelector = (
  parentEntityType?: string,
  doActivityV2?: boolean
): boolean => {
  if (doActivityV2) {
    return parentEntityType === 'school'
  }

  return true
}

interface ClassActionsProps {
  /**
   * Modal type, edit existing class, create or delete
   */
  type: ModalTypes
  /**
   * Apollo client
   */
  client: ApolloClient<object>
  /**
   * Entity Id, only needed on edit
   */
  entityId?: number
  /**
   * Entity name, only needed on delete
   */
  entityName?: string
  /**
   * Entity degree, only needed on delete
   */
  entityDegree?: Degree
  /**
   * Parent entity Id, only needed on entity creation
   */
  parentId?: number
  /**
   * Parent entity type, used to determine if creating a class
   */
  parentEntityType?: string
  /**
   * Indicates if should display modal or not
   */
  isOpen: boolean
  /**
   * Modal onClose function
   */
  onClose: () => void
}

const ClassActions: FunctionComponent<ClassActionsProps> = ({
  type,
  client,
  entityId,
  entityName,
  entityDegree,
  parentId,
  parentEntityType,
  isOpen,
  onClose
}) => {
  const { canManageEntities } = usePermissions()
  const { t } = useTranslation()
  const { featureToggles } = useFeatureToggles()

  const {
    modalTitle,
    buttonLabel,
    selectorItems,
    canSubmitEntity,
    handleSubmit,
    handleEntityName,
    handleEntitySchollarYear
  } = useClassModal({
    type,
    client,
    entityId,
    parentId,
    parentEntityType,
    defaultEntityName: entityName,
    defaultEntityDegree: entityDegree
  })

  return (
    <Modal
      data-testid={`class-modal-${type}`}
      show={isOpen}
      canCloseByXBtn
      title={t(modalTitle as LanguageTokens)}
      onClose={onClose}
    >
      {[ModalTypes.edit, ModalTypes.create].includes(type) ? (
        <Wrapper>
          {selectorItems &&
            shouldShowSchoolYearSelector(
              parentEntityType,
              featureToggles?.doActivityV2
            ) && (
              <Select
                disabled={!canManageEntities}
                data-testid={'degree-selector'}
                placeholder={t('Ano escolar')}
                items={selectorItems}
                onChange={item => handleEntitySchollarYear(item.id)}
                defaultOption={
                  entityDegree
                    ? selectorItems.find(item => item.id === entityDegree)
                    : undefined
                }
              />
            )}
          <Input
            data-testid={'name-input'}
            disabled={!canManageEntities}
            placeholder={t('Nome')}
            defaultValue={entityName}
            onChange={e => handleEntityName(e.target.value)}
          />
        </Wrapper>
      ) : (
        <Text>
          {t('Tem certeza que deseja remover a turma')} <b>{entityName}</b>?{' '}
          {''}
          {t(
            'Os usuários e turmas atrelados a essa turma também serão deletados.'
          )}
        </Text>
      )}
      <RightButton
        disabled={!canSubmitEntity || !canManageEntities}
        data-testid={'submit-button'}
        style={{ width: '100px' }}
        theme={{ type: 'primaryLivros', size: 'large' }}
        text={t(buttonLabel as LanguageTokens)}
        onClick={() => handleSubmit()}
      />
    </Modal>
  )
}

export default ClassActions
